import { Checkbox, Form, Input, Modal, Select } from 'antd';
import { useSuccessMessage } from 'hooks';
import { useEffect } from 'react';
import { useGetHospitalsQuery } from 'services/hospitals.service';
import { EditUserParams, useEditUserMutation, useGetRolesQuery } from 'services/users.service';
import { BaseModalProps, Hospital, QueryResponse, REGULAR_EXPRESSIONS, VegRole, VegUser } from 'utils/types';

enum EDIT_USER_FIELDS {
    PHONE_NUMBER = 'phone_number',
    FIRST_NAME = 'first_name',
    LAST_NAME = 'last_name',
    IS_ACTIVE = 'is_active',
    ROLE_ID = 'role_id',
    OKTA_SUBJECT = 'okta_subject',
    PAYROLL_ID = 'payroll_id',
    ASSIGNED_HOSPITALS = 'assigned_hospitals',
}

interface EditUserModalProps extends BaseModalProps {
    user: VegUser | null;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, setIsOpen, user }) => {
    const [form] = Form.useForm<EditUserParams>();
    const { data: userRoles } = useGetRolesQuery(null);
    const { data: hospitals } = useGetHospitalsQuery<QueryResponse<Hospital[]>>({});
    const [editUser, { isSuccess, isError, isLoading }] = useEditUserMutation();

    useEffect(() => {
        if (user) {
            form.setFieldsValue(user);
        }
    }, [user]);

    const handleClose = () => {
        setIsOpen(false);
    };

    useSuccessMessage({
        success: {
            status: isSuccess,
            message: `User '${form.getFieldValue(EDIT_USER_FIELDS.FIRST_NAME)} ${form.getFieldValue(
                EDIT_USER_FIELDS.LAST_NAME,
            )}' has been succesfully updated.`,
            callback: handleClose,
        },
        error: {
            status: isError,
            message: `It seems there has been an issue updating the user'${form.getFieldValue(
                EDIT_USER_FIELDS.FIRST_NAME,
            )} ${form.getFieldValue(EDIT_USER_FIELDS.LAST_NAME)}', please try again.`,
        },
    });

    const handleSave = (values: EditUserParams) => {
        editUser({
            ...values,
            // Set phone number to null if not entered.
            phone_number: typeof values.phone_number === 'string' && !values.phone_number.length ? null : values.phone_number,
            id: (user as VegUser)?.id,
            hospital_bypass: (user as VegUser)?.hospital_bypass,
        });
    };

    return (
        <Modal title='Edit User' open={isOpen} onOk={form.submit} onCancel={handleClose} okText='Save' confirmLoading={isLoading}>
            <Form
                form={form}
                name='basic'
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                initialValues={user !== null ? user : {}}
                onFinish={handleSave}
            >
                <Form.Item label='Email'>{user?.email}</Form.Item>
                <Form.Item
                    label='First Name'
                    name={EDIT_USER_FIELDS.FIRST_NAME}
                    rules={[
                        {
                            required: true,
                            message: "Please ensure the user's firstname has been entered.",
                        },
                    ]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    label='Last Name'
                    name={EDIT_USER_FIELDS.LAST_NAME}
                    rules={[
                        {
                            required: true,
                            message: "Please ensure the user's last name has been entered.",
                        },
                    ]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    label='Phone No.'
                    name={EDIT_USER_FIELDS.PHONE_NUMBER}
                    rules={[
                        {
                            pattern: RegExp(REGULAR_EXPRESSIONS.PHONE_NO),
                            message: "Please ensure the user's phone number has the correct format.",
                        },
                    ]}
                >
                    <Input />
                </Form.Item>
                <Form.Item label='Role ID' name={EDIT_USER_FIELDS.ROLE_ID}>
                    <Select>
                        {userRoles?.map((role: VegRole) => {
                            return (
                                <Select.Option key={role.name} value={role.role_id}>
                                    {role.name}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </Form.Item>
                <Form.Item label='Locations' name={EDIT_USER_FIELDS.ASSIGNED_HOSPITALS}>
                    <Select mode='multiple'>
                        {hospitals?.map((hospital: Hospital) => {
                            return (
                                <Select.Option key={hospital.display_name} value={hospital.id}>
                                    {hospital.display_name}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </Form.Item>
                <Form.Item label='Okta Subject' name={EDIT_USER_FIELDS.OKTA_SUBJECT}>
                    <Input />
                </Form.Item>
                <Form.Item label='Payroll ID' name={EDIT_USER_FIELDS.PAYROLL_ID}>
                    <Input />
                </Form.Item>
                <Form.Item label='Active' name={EDIT_USER_FIELDS.IS_ACTIVE} valuePropName='checked'>
                    <Checkbox />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EditUserModal;
